import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { regionalSalesManagersQueryOptions } from '@/features/users/api/users.api';
import { getRegionalSalesManagerFilterField } from '@/utils/filterFields';
import { mpRegionsQueryOptions } from '../api/marketPromotion.api';
import { MpAreaFilterPanel } from '../components/MpAreaFilterPanel';
import { getMpRegionColumns } from '../components/MpColumns';
import { useMpRegionFilters } from '../hooks/useMpRegionFilters';

export function MpRegionSummaryPage() {
  const controller = useMpRegionFilters();
  const { data, isLoading, error, refetch } = useQuery(mpRegionsQueryOptions(controller.params));
  const { data: regionalSalesManagers } = useQuery(regionalSalesManagersQueryOptions());

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Region-Wise Market Promotion"
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <MpAreaFilterPanel
          filters={controller}
          personField={getRegionalSalesManagerFilterField(regionalSalesManagers, 'rsmUserId')}
        />
      </FilterPanelCollapse>

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the regions"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={data ?? []}
          columns={getMpRegionColumns()}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="regionName"
          emptyMessage="No region matches"
        />
      )}
    </div>
  );
}
